import React, { useState, useEffect } from 'react';
import { Settings, Mail, Bell, Shield, Calendar, FileText, Send, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const EmailSettings = () => {
  const [prefs, setPrefs] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prefsRes, logRes] = await Promise.all([
        api.get('/admin/email-preferences'),
        api.get('/admin/email-log')
      ]);
      setPrefs(prefsRes.data);
      setEmailLog(logRes.data || []);
    } catch (error) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/email-preferences', prefs);
      setPrefs(res.data);
      toast.success('Email preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const togglePref = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      await api.post('/admin/test-email');
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const triggerCheck = async (type) => {
    try {
      if (type === 'birthday') {
        await api.post('/admin/send-birthday-emails');
        toast.success('Birthday check completed');
      } else {
        await api.post('/admin/send-contract-expiry-emails');
        toast.success('Contract expiry check completed');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to run check');
    }
  };

  const ToggleSwitch = ({ enabled, onToggle, testId }) => (
    <button
      onClick={onToggle}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
        enabled ? 'bg-white' : 'bg-zinc-700'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
        enabled ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  );

  if (loading || !prefs) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            Email Settings
          </h1>
          <p className="text-lg text-zinc-300">Manage your email notification preferences</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12 space-y-6">
        {/* Birthday Reminders */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-pink-500/10 rounded-lg">
              <Calendar className="w-5 h-5 text-pink-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white">Birthday Reminders</h2>
              <p className="text-sm text-zinc-400">Get notified about player birthdays</p>
            </div>
            <ToggleSwitch enabled={prefs.birthday_reminders} onToggle={() => togglePref('birthday_reminders')} testId="toggle-birthday-reminders" />
          </div>
          {prefs.birthday_reminders && (
            <div className="space-y-4 pl-12">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">1 week before</p>
                  <p className="text-zinc-500 text-xs">Receive an email 7 days before a birthday</p>
                </div>
                <ToggleSwitch enabled={prefs.birthday_week_before} onToggle={() => togglePref('birthday_week_before')} testId="toggle-birthday-week" />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">On the day</p>
                  <p className="text-zinc-500 text-xs">Receive an email on the birthday</p>
                </div>
                <ToggleSwitch enabled={prefs.birthday_on_day} onToggle={() => togglePref('birthday_on_day')} testId="toggle-birthday-day" />
              </div>
            </div>
          )}
        </div>

        {/* Contract Expiry Alerts */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <FileText className="w-5 h-5 text-red-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white">Contract Expiry Alerts</h2>
              <p className="text-sm text-zinc-400">Get notified when player contracts are expiring</p>
            </div>
            <ToggleSwitch enabled={prefs.contract_expiry_alerts} onToggle={() => togglePref('contract_expiry_alerts')} testId="toggle-contract-alerts" />
          </div>
          {prefs.contract_expiry_alerts && (
            <div className="space-y-4 pl-12">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">90 days before</p>
                  <p className="text-zinc-500 text-xs">Early warning for contract planning</p>
                </div>
                <ToggleSwitch enabled={prefs.contract_expiry_90_days} onToggle={() => togglePref('contract_expiry_90_days')} testId="toggle-contract-90" />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">60 days before</p>
                  <p className="text-zinc-500 text-xs">Mid-range contract alert</p>
                </div>
                <ToggleSwitch enabled={prefs.contract_expiry_60_days} onToggle={() => togglePref('contract_expiry_60_days')} testId="toggle-contract-60" />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">30 days before</p>
                  <p className="text-zinc-500 text-xs">Urgent contract expiry warning</p>
                </div>
                <ToggleSwitch enabled={prefs.contract_expiry_30_days} onToggle={() => togglePref('contract_expiry_30_days')} testId="toggle-contract-30" />
              </div>
            </div>
          )}
        </div>

        {/* Representation Expiry */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Shield className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white">Representation Expiry Alerts</h2>
              <p className="text-sm text-zinc-400">Get notified when agency representation contracts expire</p>
            </div>
            <ToggleSwitch enabled={prefs.representation_expiry_alerts} onToggle={() => togglePref('representation_expiry_alerts')} testId="toggle-rep-alerts" />
          </div>
        </div>

        {/* Save + Actions */}
        <div className="flex flex-wrap gap-3">
          <button onClick={savePrefs} disabled={saving} data-testid="save-email-prefs-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide text-sm rounded-sm transition-all disabled:opacity-50">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
          <button onClick={sendTestEmail} disabled={sendingTest} data-testid="send-test-email-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white hover:bg-white/20 font-bold uppercase tracking-wide text-sm rounded-sm transition-all disabled:opacity-50">
            <Send className="w-4 h-4" />
            {sendingTest ? 'Sending...' : 'Send Test Email'}
          </button>
          <button onClick={() => triggerCheck('birthday')} data-testid="trigger-birthday-check-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white hover:bg-white/20 font-bold uppercase tracking-wide text-sm rounded-sm transition-all">
            <Calendar className="w-4 h-4" />
            Check Birthdays Now
          </button>
          <button onClick={() => triggerCheck('contract')} data-testid="trigger-contract-check-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white hover:bg-white/20 font-bold uppercase tracking-wide text-sm rounded-sm transition-all">
            <FileText className="w-4 h-4" />
            Check Contracts Now
          </button>
        </div>

        {/* Email Log */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Mail className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-bold uppercase tracking-tight text-white">Recent Email Activity</h2>
          </div>
          {emailLog.length === 0 ? (
            <p className="text-zinc-500 text-sm">No emails sent yet. Emails will appear here once the scheduler runs.</p>
          ) : (
            <div className="space-y-2">
              {emailLog.map((log, i) => {
                const typeLabels = {
                  birthday_today: { label: 'Birthday (Today)', color: 'text-pink-400', bg: 'bg-pink-500/10' },
                  birthday_week: { label: 'Birthday (1 Week)', color: 'text-pink-400', bg: 'bg-pink-500/10' },
                  contract_30: { label: 'Contract (30 days)', color: 'text-red-400', bg: 'bg-red-500/10' },
                  contract_60: { label: 'Contract (60 days)', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                  contract_90: { label: 'Contract (90 days)', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                  representation_30: { label: 'Representation (30d)', color: 'text-purple-400', bg: 'bg-purple-500/10' },
                  representation_60: { label: 'Representation (60d)', color: 'text-purple-400', bg: 'bg-purple-500/10' },
                  representation_90: { label: 'Representation (90d)', color: 'text-purple-400', bg: 'bg-purple-500/10' },
                  today: { label: 'Birthday (Today)', color: 'text-pink-400', bg: 'bg-pink-500/10' },
                  week: { label: 'Birthday (1 Week)', color: 'text-pink-400', bg: 'bg-pink-500/10' },
                };
                const config = typeLabels[log.type] || { label: log.type, color: 'text-zinc-400', bg: 'bg-zinc-500/10' };
                return (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg" data-testid={`email-log-${i}`}>
                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${config.bg} ${config.color}`}>{config.label}</span>
                    <span className="text-white text-sm flex-1">{(log.players || []).join(', ')}</span>
                    <span className="text-zinc-500 text-xs">{new Date(log.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
